import { useEffect, useId, useState, type KeyboardEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown } from 'lucide-react'
import { usePopover } from './usePopover'

export type SelectOption = {
  value: string
  label: string
  icon?: ReactNode
}

/**
 * Accessible single-select dropdown. Focus stays on the trigger and the active
 * option is announced through aria-activedescendant, like a native select.
 * Label it with `<label htmlFor={id}>`.
 */
export function Select({
  id,
  value,
  options,
  onChange,
  disabled = false,
}: {
  id: string
  value: string
  options: SelectOption[]
  onChange: (value: string) => void
  disabled?: boolean
}) {
  const popover = usePopover<HTMLButtonElement>()
  const listId = useId()
  const [active, setActive] = useState(-1)
  const selectedIndex = options.findIndex((option) => option.value === value)
  const selected = options[selectedIndex]
  const optionId = (index: number) => `${listId}-${index}`

  useEffect(() => {
    if (popover.isOpen && active >= 0) {
      document.getElementById(optionId(active))?.scrollIntoView?.({ block: 'nearest' })
    }
  })

  function openList() {
    setActive(Math.max(selectedIndex, 0))
    popover.open()
  }

  function choose(index: number) {
    const option = options[index]
    popover.close()
    if (option && option.value !== value) onChange(option.value)
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const last = options.length - 1

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault()
        const step = event.key === 'ArrowDown' ? 1 : -1
        if (popover.isOpen) setActive((index) => Math.min(Math.max(index + step, 0), last))
        else openList()
        return
      }
      case 'Home':
      case 'End':
        if (!popover.isOpen) return
        event.preventDefault()
        setActive(event.key === 'Home' ? 0 : last)
        return
      case 'Enter':
      case ' ':
        event.preventDefault()
        if (popover.isOpen) choose(active)
        else openList()
        return
      case 'Escape':
        if (!popover.isOpen) return
        // Keep a surrounding modal from closing along with the list.
        event.preventDefault()
        event.stopPropagation()
        popover.close()
        return
      case 'Tab':
        popover.close(false)
        return
      default:
        if (popover.isOpen && event.key.length === 1) {
          const key = event.key.toLowerCase()
          const match = options.findIndex((option, index) => index > active && option.label.toLowerCase().startsWith(key))
          const wrapped = match >= 0 ? match : options.findIndex((option) => option.label.toLowerCase().startsWith(key))
          if (wrapped >= 0) setActive(wrapped)
        }
    }
  }

  return (
    <>
      <button
        aria-activedescendant={popover.isOpen && active >= 0 ? optionId(active) : undefined}
        aria-controls={popover.isOpen ? listId : undefined}
        aria-expanded={popover.isOpen}
        aria-haspopup="listbox"
        className="wt-select-trigger"
        data-open={popover.isOpen || undefined}
        disabled={disabled}
        id={id}
        onClick={() => (popover.isOpen ? popover.close() : openList())}
        onKeyDown={onKeyDown}
        ref={popover.triggerRef}
        role="combobox"
        type="button"
      >
        <span className="wt-select-value">
          {selected?.icon}
          <span>{selected?.label ?? ''}</span>
        </span>
        <ChevronDown aria-hidden="true" className="wt-select-chevron" size={15} />
      </button>

      {popover.phase !== 'closed'
        ? createPortal(
            <div className="wt-popover wt-select-panel" data-state={popover.phase} ref={popover.panelRef} style={popover.style}>
              <ul id={listId} role="listbox">
                {options.map((option, index) => (
                  <li
                    aria-selected={index === selectedIndex}
                    className="wt-select-option"
                    data-active={index === active || undefined}
                    id={optionId(index)}
                    key={option.value}
                    onClick={() => choose(index)}
                    // Keeps focus on the trigger so keyboard state is not lost.
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setActive(index)}
                    role="option"
                  >
                    {option.icon}
                    <span>{option.label}</span>
                    {index === selectedIndex ? <Check aria-hidden="true" className="wt-select-check" size={14} /> : null}
                  </li>
                ))}
              </ul>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
